import React, { useState, Fragment, useEffect } from "react";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import MuiLink from "@mui/material/Link";
import Loader from "components/Progress";
import Card from "@mui/material/Card";
import Table from "components/Table/newTable";
import Checkbox from "@mui/material/Checkbox";
import { CardContent } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import DeleteAlert from "components/Alerts/Delete";
import Button from "@mui/material/Button";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import Tooltip from "@mui/material/Tooltip";
import { CSVLink } from "react-csv";
import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Collapse from "@mui/material/Collapse";
import {
  useBatchUpdateAlarmMutation,
  useGetAlarmsQuery,
} from "../../../services/alarms";
import hexRgb from "hex-rgb";
import { useGetDevicesQuery } from "services/devices";
import AlarmHistory from "../History";

// The dashboard filters as request parameters, shared by every alarm request
// on the page (both count cards, the trend, the list and the timeline) so they
// all ask for the same alarms. Returns the pieces joined with "&"; the caller
// adds its own paging and sort.
//   serviceId: the embedded dashboard's one solution, sent as it is.
//   services: the user's solutions (metaDataValue.services).
//   withStatus: false leaves out status, e-mails and actuations (the alarm
//     log has none of them).
//   dates: { startTime, endTime } in place of the filter's range (the trend's
//     two weeks, the timeline's 31-day clamp).
// Until the user picks an End Time, the list and the cards have no end: the
// range was fixed when the page opened, and a new raise moves an alarm's
// updatedAt past it (code review 2026-11-02).
// Solutions: the picked names still accessible, or every accessible solution
// when none is picked or none of the picks resolves. serviceId is never left
// out while the user has solutions: without it the server reads the whole
// tenant, past the user's role. groupId: the `serviceId:groupId` pairs of those
// solutions that have a group.
export const alarmsQuery = (
  alarmsFilter,
  { serviceId, services = [], withStatus = true, dates } = {}
) => {
  const range = dates || alarmsFilter.date;
  const noEnd = !dates && !alarmsFilter.date.endPicked;
  const text = alarmsFilter.search.text.trim();
  const picked = services.filter((s) => alarmsFilter.solutions.includes(s.name));
  const scope = serviceId
    ? services.filter((s) => s.id == serviceId)
    : picked.length
    ? picked
    : services;
  const groupIds = scope
    .filter((s) => s.group?.id)
    .map((s) => `${s.id}:${s.group.id}`);
  const pieces = [];
  if (alarmsFilter.priority.length)
    pieces.push(`priority=${JSON.stringify(alarmsFilter.priority)}`);
  if (withStatus && alarmsFilter.status.length)
    pieces.push(`status=${JSON.stringify(alarmsFilter.status)}`);
  if (withStatus && alarmsFilter.emails) pieces.push(`emails=true`);
  if (withStatus && alarmsFilter.actuations) pieces.push(`actuations=true`);
  if (text)
    pieces.push(
      `search=${encodeURIComponent(text)}&searchBy=${JSON.stringify(
        alarmsFilter.search.by
      )}`
    );
  pieces.push(
    `dateFrom=${new Date(
      new Date(range.startTime).setSeconds(0)
    ).toISOString()}${
      noEnd
        ? ""
        : `&dateTo=${new Date(new Date(range.endTime).setSeconds(0)).toISOString()}`
    }`
  );
  if (serviceId) pieces.push(`serviceId=${serviceId}`);
  else if (scope.length)
    pieces.push(
      `serviceId=${
        picked.length == 1
          ? picked[0].id
          : JSON.stringify(scope.map((s) => s.id))
      }`
    );
  if (groupIds.length) pieces.push(`groupId=${JSON.stringify(groupIds)}`);
  return pieces.join("&");
};

export default function List({
  generateRows,
  allAlarms,
  csvData,
  serviceId,
  viewToggle,
  fill
}) {
  let keys = [
    { name: "createdAt", key: "createdAt" },
    {
      name: "updatedAt",
      key: "updatedAt",
    },
    {
      name: "deviceName",
      key: "deviceName",
    },
    {
      name: "count",
      key: "count",
    },
  ];
  let token = window.localStorage.getItem("token");
  const { enqueueSnackbar } = useSnackbar();
  const alarmsFilter = useSelector((state) => state.alarmsFilter);
  const metaDataValue = useSelector((state) => state.metaData);
  // Find the tab BY NAME: the module now carries extra sub-tabs (e.g. "Scheduled
  // Reports"), so tabs[0] is no longer guaranteed to be the "Alarms Management" tab.
  let permission =
    metaDataValue.modulePermissions?.["Solution Features"]?.Alarms;
  // Fail CLOSED: bulk acknowledge/clear needs Solution Features > Alarms explicitly at
  // ALL (super admins bypass). The old check only suppressed on READ, so a role WITHOUT
  // the tab at all (permission undefined) wrongly got the checkboxes.
  const canBulkAct = !!metaDataValue.unscopedAdmin || permission === "ALL";
  const [rgb, setRgb] = React.useState(
    hexRgb(metaDataValue.branding.secondaryColor)
  );
  const [columns, setColumns] = React.useState(generateColumns());
  const [sort, setSort] = useState({
    createdAt: 1,
    updatedAt: 1,
    deviceName: 1,
    service: 1,
    count: 1,
  });
  const [sortedFields, setSortedFields] = useState({ updatedAt: -1 });
  const [checked, setChecked] = useState([]);
  const [status, setStatus] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [totalDocumentRows,setTotalDocumentRows]=useState([])
  const [totalPages, setTotalPages] = React.useState(0);
  const [totalDocuments, setTotalDocuments] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [updateBatchAlarm, result] = useBatchUpdateAlarmMutation();
  const filterParams = alarmsQuery(alarmsFilter, {
    serviceId,
    services: metaDataValue.services,
  });
  // Back to page 1 when the request changes. Keyed on the request, not the
  // whole filter, so a change that asks for nothing new (e.g. "Search by" with
  // an empty search box) keeps the page and the open row (code review
  // 2026-10-30).
  useEffect(() => {
    setPage(1);
  }, [filterParams]);
  // The ticks clear when the query changes, not on a page change (code review
  // 2026-10-30).
  useEffect(() => {
    setChecked([]);
  }, [filterParams, sortedFields]);
  // the row whose history is open (one at a time), closed whenever the
  // query changes (page, sort, filters) and after a batch action
  const [openId, setOpenId] = useState(null);
  useEffect(() => {
    setOpenId(null);
  }, [filterParams, page, sortedFields]);
  // The open row's panel is as wide as the table's visible area (the table
  // can scroll sideways): newTable's scrolling container less its vertical
  // scrollbar, found in the wrapper around the table (the first one; the
  // panel's own list comes later). Looked up again when the table appears.
  const [tableBox, setTableBox] = useState(null);
  const [visibleWidth, setVisibleWidth] = useState(0);
  const hasRows = rows.length > 0;
  useEffect(() => {
    if (!tableBox) return undefined;
    const container =
      tableBox.querySelector(".MuiTableContainer-root") || tableBox;
    const measure = () => setVisibleWidth(container.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    // measured on the next frame, so the new width never resizes what is
    // observed within the same frame
    let frame;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    observer.observe(container);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [tableBox, hasRows]);
  const paginatedAlarms = 
  useGetAlarmsQuery({
    token: window.localStorage.getItem("token"),
    params: `?${
      Object.keys(sortedFields).length
        ? `sort=${JSON.stringify(sortedFields)}&`
        : ``
    }${filterParams}&pageSize=10&withTotalPages=true&currentPage=${page}`,
  });


  //fetching all documents instead of paginating
  const allAlarmsList = 
  useGetAlarmsQuery({
    token: window.localStorage.getItem("token"),
    params: `?allalarms=true&${
      Object.keys(sortedFields).length
        ? `sort=${JSON.stringify(sortedFields)}&`
        : ``
    }${filterParams}&pageSize=${totalDocuments==0?10:totalDocuments}&withTotalPages=true&currentPage=1`,
  },{skip: paginatedAlarms.isFetching || totalDocuments==null});

  //storing all documents
  useEffect(()=>{
    if(!allAlarmsList.isFetching && allAlarmsList.isSuccess){
      let data=JSON.parse(JSON.stringify(allAlarmsList.data.payload))
      const rows=generateRows(data.data)
      setTotalDocumentRows(rows)
      // a tick on an alarm closed or gone since (another user, a rule) goes
      // (code review 2026-11-02)
      const open = new Set(rows.filter((row)=>row.status != "CLEARED").map((row)=>row.alarmId))
      setChecked((ticks)=>ticks.filter((id)=>open.has(id)))
    } else if (!allAlarmsList.isFetching && allAlarmsList.isError) {
      // e.g. more than 2,000 matching alarms: no select-all list from another
      // filter remains (code review 2026-10-30)
      setTotalDocumentRows([])
      setChecked([])
    }
  },[totalDocuments, allAlarmsList.isFetching])


  useEffect(() => {
    if (!paginatedAlarms.isFetching && paginatedAlarms.isSuccess) {
      if(Object.keys(paginatedAlarms.data.payload).length){
        let data = JSON.parse(JSON.stringify(paginatedAlarms.data.payload));
        setColumns(generateColumns());
        setTotalPages(data.totalPages);
        setTotalDocuments(data.totalDocuments);
        setRows(generateRows(data.data));
        // the last page emptied (e.g. after a batch clear): the new last page
        // (code review 2026-11-02)
        if (!data.data?.length && data.totalPages && page > data.totalPages) {
          setPage(data.totalPages);
        }
      }
      else{
        setColumns(generateColumns());
        setTotalPages(0);
        setTotalDocuments(0);
        setRows([]);
      }
    } else if (!paginatedAlarms.isFetching && paginatedAlarms.isError) {
      // a failed read leaves no earlier rows or ticks behind, as the Timeline
      // (code review 2026-11-02)
      setTotalPages(0);
      setTotalDocuments(null);
      setRows([]);
      setChecked([]);
      showSnackbar(
        "Alarms",
        paginatedAlarms.error?.data?.message || "Could not load the alarms",
        "error",
        1000
      );
    }
  }, [paginatedAlarms.isFetching]);

  function handlePageChange(page) {
    setPage(page);
  }

  // a closed alarm cannot be selected, in the header box as in its own row
  // (code review 2026-11-02)
  const selectableIds = totalDocumentRows
    .filter((row) => row.status != "CLEARED")
    .map((row) => row.alarmId);

  const toggleCheck = (chk, row) => {
    if (row != "all") {
      let tempChecked = [...checked];
      if (chk) {
        tempChecked.push(row.alarmId);
      } else {
        let ind = tempChecked.findIndex((t) => t == row.alarmId);
        tempChecked.splice(ind, 1);
      }
      setChecked(tempChecked);
    } else {
      if (chk) {
        setChecked(selectableIds);
      } else {
        setChecked([]);
      }
    }
  };

  useEffect(() => {
    let tempSortedFields = { ...sortedFields };
    keys.forEach((key) => {
      let val = sort[key.name];
      if (
        val == 1 &&
        tempSortedFields[key.key] &&
        tempSortedFields[key.key] != val
      ) {
        tempSortedFields = { [key.key]: val };
      }
      if (val == -1) {
        tempSortedFields = { [key.key]: val };
      }
    });
    if (JSON.stringify(sortedFields) != JSON.stringify(tempSortedFields)) {
      setSortedFields(tempSortedFields);
    }
  }, [sort]);

  function generateColumns() {
    let tempColumns = [
      // the history arrow (every user) and, left to right, the bulk checkbox
      { id: "html", label: "html", align: "center" },
      {
        id: "service",
        label: "Solution",
        align: "center",
      },
      {
        id: "deviceName",
        label: "Asset",
        align: "center",
      },
      { id: "severity", label: "Priority", align: "center" },
      { id: "status", label: "Status", align: "center" },
      { id: "count", label: "Repeated", align: "center" },
      { id: "type", label: "Rule", align: "center" },
      { id: "reason", label: "Reason", align: "center" },
      { id: "email", label: "Emails", align: "center" },
      { id: "actuations", label: "Actuations", align: "center" },
      { id: "createdAt", label: "Created At", align: "center" },
      { id: "updatedAt", label: "Updated At", align: "center" },
    ];
    return tempColumns;
  }

  // The first cell: the bulk checkbox, then the history arrow (owner,
  // 2026-10-30). A click on either does only its own job (the row click would
  // toggle the history too).
  const check = (a) => {
    return (
      <span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        {canBulkAct ? (
          <Checkbox
            checked={checked.includes(a.alarmId)}
            onChange={(e) => toggleCheck(e.target.checked, a)}
            onClick={(e) => e.stopPropagation()}
            disabled={a.status == "CLEARED"}
          />
        ) : null}
        {expandArrow(a)}
      </span>
    );
  };

  const checkAll = (a) => {
    if (!canBulkAct) {
      return null;
    }
    // offered only when the select-all read succeeded for this query, so it
    // never acts on another filter's alarms (code review 2026-10-30)
    // the trailing space (the arrow's width) lines the box up with the rows' checkboxes
    return (
      paginatedAlarms.isSuccess &&
      allAlarmsList.isSuccess &&
      !allAlarmsList.isError &&
      !allAlarmsList.isFetching ?
      <span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Checkbox
          checked={selectableIds.length > 0 && checked.length == selectableIds.length}
          disabled={!selectableIds.length}
          onChange={(e) => toggleCheck(e.target.checked, "all")}
        />
        <span style={{ width: "34px" }} />
      </span>
      : null
    );
  };

  const expandArrow = (a) => {
    const open = a.alarmId === openId;
    return (
      <IconButton
        size="small"
        aria-label={open ? "Hide history" : "Show history"}
        onClick={(e) => {
          e.stopPropagation();
          setOpenId(open ? null : a.alarmId);
        }}
      >
        {/* one arrow that turns as the history opens and closes */}
        <KeyboardArrowDownIcon
          sx={{ transition: "transform 250ms ease", transform: open ? "rotate(180deg)" : "none" }}
        />
      </IconButton>
    );
  };

  // Each row's history slides open and closed under it (2026-10-30, owner); the
  // panel is mounted only while open or closing, and opening it does not scroll
  // the page (owner). The table row holds a link
  // element in deviceName, so the panel gets the plain row by alarmId. Nested
  // look: the panel starts right of the checkbox (cell inset 16 + box 42).
  const indent = canBulkAct ? 58 : 16;
  const renderExpanded = (row) => {
    const plainRow = rows.find((r) => r.alarmId === row.alarmId);
    if (!plainRow) return null;
    return (
      <Collapse
        in={row.alarmId === openId}
        timeout={300}
        unmountOnExit
      >
        <AlarmHistory
          alarm={plainRow}
          serviceId={serviceId || plainRow.serviceId}
          width={visibleWidth}
          indent={indent}
        />
      </Collapse>
    );
  };

  function generateQuestion(tempStatus) {
    if (tempStatus ? tempStatus == "CLEARED" : status == "CLEARED") {
      let clearCount = 0;
      checked.forEach((chk) => {
        if (allAlarms.find((a) => a.alarmId == chk)?.status == "CLEARED") {
          clearCount += 1;
        }
      });
      return checked.length - clearCount
        ? `${checked.length - clearCount} out of ${
            checked.length
          } alarms will be cleared. Are you sure you want to clear alarms?`
        : false;
    } else {
      let ackCount = 0;
      checked.forEach((chk) => {
        if (
          allAlarms
            .filter((alarm) => alarm.status != "CLEARED")
            .find((a) => a.alarmId == chk)?.status == "ACKNOWLEDGED"
        ) {
          ackCount += 1;
        }
      });
      return checked.length - ackCount
        ? `${checked.length - ackCount} out of ${
            checked.length
          } alarms will be acknowledged. Are you sure you want to acknowledge alarms?`
        : false;
    }
  }

  async function updateAlarms() {
    let updated = await updateBatchAlarm({
      token,
      alarmIds: checked,
      status,
    });
    if (updated.data?.success) {
      showSnackbar("Alarms", updated.data?.message, "success", 1000);
      setStatus("");
      setChecked([]);
      setOpenId(null);
    } else {
      showSnackbar(
        "Alarms",
        updated.data?.message || updated.error?.data?.message || "Could not update the alarms",
        "error",
        1000
      );
    }
  }

  const icon = (row, type) => {
    let tempIcon;
    if (type == "email" && row.email && row.email.filter((e) => e).length) {
      tempIcon = (
        <Tooltip
          title={
            <span>
              <p>Email(s) sent to</p>
              {row.email
                .filter((e) => e)
                .map((e) => (
                  <p>○ {e}</p>
                ))}
            </span>
          }
          placement="top"
          arrow
          // TransitionComponent={Zoom}
        >
          <ForwardToInboxIcon />
        </Tooltip>
      );
    } else if (
      type == "actuations" &&
      row.actuations &&
      row.actuations.filter((a) => a)
    ) {
      tempIcon = (
        <Tooltip
          title={
            <span>
              {row.actuations
                .filter((a) => a)
                .map((e) => (
                  <p>{e}</p>
                ))}
            </span>
          }
          placement="top"
          arrow
          // TransitionComponent={Zoom}
        >
          <FlashOnIcon />
        </Tooltip>
      );
    }
    // a click on an icon does not open the row's history
    return tempIcon ? <span onClick={(e) => e.stopPropagation()}>{tempIcon}</span> : tempIcon;
  };

  function showSnackbar(title, message, variant, timeOut) {
    return enqueueSnackbar({ title, message, variant }, { timeOut });
  }

  // Render the Asset cell as a link into that device's Asset View
  // (/solutions/:serviceId/:deviceId/:tabId — deviceId is the device's
  // internalId, which the alarm carries as `sensorId`). Only linked when the
  // alarm resolves to an accessible solution; legacy alarms with no serviceId,
  // or alarms on a since-removed solution, stay as plain text. `deviceName` is
  // left untouched on `rows` itself so CSV export (built from the raw rows),
  // column sorting and the row history keep working on the plain string.
  // The table shows the current page; select-all uses `totalDocumentRows`.
  const tableRows = React.useMemo(() => {
    const knownServiceIds = new Set(
      (metaDataValue.services || []).map((s) => s.id)
    );
    return rows.map((row) => {
      const linkable =
        row.deviceName && row.sensorId && knownServiceIds.has(row.serviceId);
      if (!linkable) return row;
      return {
        ...row,
        deviceName: (
          // Match the app's hyperlink pattern (see Asset View/Alarms/Accordion):
          // brand primaryColor as text — secondaryColor was the previous choice
          // but renders unreadably on white for tenants like Rensair whose
          // secondary is yellow. MUI Link gives us underline-on-hover for free.
          <MuiLink
            component={RouterLink}
            to={`/solutions/${row.serviceId}/${row.sensorId}/0`}
            underline="hover"
            title={`Open ${row.deviceName}`}
            onClick={(e) => e.stopPropagation()}
            sx={{
              color: metaDataValue.branding.primaryColor,
              fontWeight: 500,
            }}
          >
            {row.deviceName}
          </MuiLink>
        ),
      };
    });
  }, [rows, metaDataValue.services]);

  return (
    <Fragment>
      {paginatedAlarms.isFetching || allAlarmsList.isFetching ? (
        <span
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Loader />
        </span>
      ) : (
        // fill: the card and its content are flex columns taking the area's
        // height, so the table scrolls inside and the pager stays in view
        <Card
          sx={{
            borderRadius: "10px",
            height: "100%",
            ...(fill ? { display: "flex", flexDirection: "column" } : {}),
          }}
        >
          <CardContent
            sx={{
              padding: "0px",
              ...(fill
                ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }
                : {}),
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "16px",
                backgroundColor: checked.length
                  ? `rgb(${rgb.red}, ${rgb.green}, ${rgb.blue},0.1)`
                  : "",
              }}
            >
              <p
                style={{
                  color: "#bfbec8",
                  fontSize: "15px",
                  fontWeight: "bold",
                }}
              >
                List
                {/* a long-running fault stops moving updatedAt, so it leaves this range while still open */}
                <span
                  style={{
                    fontWeight: "normal",
                    fontSize: "12px",
                    color: "#9f9f9f",
                    marginLeft: "8px",
                  }}
                >
                  Shows alarms updated in this period. Set Start Time back to see older open alarms.
                </span>
                {checked.length ? (
                  <span style={{ fontWeight: "normal" }}>
                    {` ( ${checked.length} selected )`}
                  </span>
                ) : null}
              </p>
              {viewToggle}

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                }}
              >
                {generateQuestion("ACKNOWLEDGED") ? (
                  <Button
                    onClick={() => setStatus("ACKNOWLEDGED")}
                    color="success"
                    variant="contained"
                    style={{
                      visibility: checked.length ? "initial" : "hidden",
                      height: "27px",
                    }}
                  >
                    BATCH ACKNOWLEDGE
                  </Button>
                ) : null}
                {generateQuestion("CLEARED") ? (
                  <Button
                    onClick={() => setStatus("CLEARED")}
                    color="error"
                    variant="contained"
                    style={{
                      visibility: checked.length ? "initial" : "hidden",
                      height: "27px",
                    }}
                  >
                    BATCH CLEAR
                  </Button>
                ) : null}
                {csvData.length ? (
                  <CSVLink
                    id="export"
                    data={csvData}
                    asyncOnClick={true}
                    filename={`Alarms Dashboard`}
                  >
                    <Tooltip title="Export to CSV" placement="top" arrow>
                      <IconButton style={{ width: "30px", height: "30px" }}>
                        <DownloadIcon
                          style={{
                            width: "25px",
                            height: "25px",
                            color: "#333",
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </CSVLink>
                ) : null}
              </div>
            </div>
            {/* measured for the row history's width; with fill it passes the
                card's remaining height on to the table */}
            <div
              ref={setTableBox}
              style={
                fill
                  ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }
                  : {}
              }
            >
              <Table
                columns={columns}
                html={check}
                htmlLabel={checkAll}
                renderExpanded={renderExpanded}
                // a click anywhere on a row opens or closes its history; selecting
                // the row's text to copy it does not
                onRowClick={(row) => {
                  if (!window.getSelection()?.toString()) {
                    setOpenId(row.alarmId === openId ? null : row.alarmId);
                  }
                }}
                rows={tableRows}
                page={page}
                totalPages={totalPages}
                totalDocuments={totalDocuments}
                handleChange={handlePageChange}
                height={fill ? undefined : "calc(100vh - 491px)"}
                fill={fill}
                pageSize={10}
                size="small"
                icon={icon}
                minHeight={"auto"}
                sort={sort}
                setSort={setSort}
                sortedFields={sortedFields}
                keys={keys}
              />
            </div>
          </CardContent>
        </Card>
      )}
      {status ? (
        <DeleteAlert
          deleteModal={true}
          question={generateQuestion()}
          platformCheck={false}
          id={status}
          handleDelete={() => updateAlarms()}
          handleClose={() => setStatus("")}
        />
      ) : null}
    </Fragment>
  );
}
